import PropTypes from 'prop-types'
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'
import { InlineConfirm } from './InlineConfirm'
import { Menu } from './Menu'

function useMedia(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const media = window.matchMedia(query)
    const changed = () => setMatches(media.matches)
    media.addEventListener('change', changed)
    return () => media.removeEventListener('change', changed)
  }, [query])
  return matches
}

// The one viewport-anchored stack per page: bulk-action bars above the save
// bar.
export function FloatingBarStack({ children, contentId }) {
  const stack = useRef(null)
  const observed = useRef({})
  // The content region can mount after the stack, so each render looks it up.
  useLayoutEffect(() => {
    const content = document.getElementById(contentId) || document.body
    if (observed.current.content === content) return
    if (observed.current.stop) observed.current.stop()
    observed.current = {
      content,
      stop: window.MEButtons.observeStack(stack.current, content),
    }
  })
  useLayoutEffect(() => () => observed.current.stop(), [])
  return createPortal(
    <div
      ref={stack}
      className="me-floating-bar-stack"
      data-me-react=""
      data-content-region={contentId}
    >
      {children}
    </div>,
    document.body
  )
}

FloatingBarStack.propTypes = {
  children: PropTypes.node.isRequired,
  // The id of the region the bars cover; it takes their height as bottom
  // padding.
  contentId: PropTypes.string,
}

function usePresence(visible) {
  const [present, setPresent] = useState(visible)
  useEffect(() => {
    if (visible) {
      setPresent(true)
      return
    }
    const timer = setTimeout(
      () => setPresent(false),
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 120
    )
    return () => clearTimeout(timer)
  }, [visible])
  return { hidden: !present, leaving: present && !visible }
}

export function StickySaveBar({
  dirty,
  onSave,
  onDiscard,
  onFailure,
  failureLabel = window.MEButtons.text('strSaveFailed'),
}) {
  const { text } = window.MEButtons
  const { hidden, leaving } = usePresence(dirty)
  const wasDirty = useRef(false)
  const [pending, setPending] = useState(false)
  useEffect(() => {
    if (dirty && !wasDirty.current)
      window.MEButtons.announce(text('strUnsavedChanges'))
    wasDirty.current = dirty
  }, [dirty])
  return (
    <div
      className={`me-floating-bar me-sticky-save-bar${
        leaving ? ' is-leaving' : ''
      }`}
      hidden={hidden}
      role="region"
      aria-label={text('strUnsavedChanges')}
    >
      <span className="me-floating-bar-message">
        {text('strUnsavedChanges')}
      </span>
      <div className="me-floating-bar-actions">
        <InlineConfirm
          aria-disabled={pending || undefined}
          data-inline-confirm={text('strDiscardQuestion')}
          data-inline-verb={text('strDiscard')}
          data-inline-cancel={text('strKeepEditing')}
          data-inline-done={text('strChangesDiscarded')}
          data-busy-label={text('strDiscarding')}
          data-failure-label={text('strDiscardFailed')}
          data-failure-text={text('strTryAgain')}
          onFailure={error => {
            // A timed-out discard may never settle; Save comes back with it.
            setPending(false)
            onFailure?.(error)
          }}
          onConfirm={() => {
            setPending(true)
            return Promise.resolve()
              .then(onDiscard)
              .finally(() => setPending(false))
          }}
        >
          {text('strDiscard')}
        </InlineConfirm>
        <Button
          className="me-btn me-btn-primary"
          aria-disabled={pending || undefined}
          data-load="success"
          data-busy-label={text('strSaving')}
          data-success-label={text('strChangesSaved')}
          data-success-text={text('strSaved')}
          data-failure-label={failureLabel}
          data-failure-text={text('strTryAgain')}
          onAction={() => {
            setPending(true)
            return onSave()
          }}
          onSuccess={() => setPending(false)}
          onFailure={error => {
            setPending(false)
            onFailure?.(error)
          }}
        >
          {text('strSaveChanges')}
        </Button>
      </div>
    </div>
  )
}

StickySaveBar.propTypes = {
  dirty: PropTypes.bool.isRequired,
  // Each returns the request's promise.
  onSave: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onFailure: PropTypes.func,
  failureLabel: PropTypes.string,
}

export function BulkActionToolbar({
  count,
  subject,
  label: suppliedLabel,
  busy = false,
  onClear,
  children,
}) {
  const { hidden, leaving } = usePresence(count > 0)
  const id = useId()
  const root = useRef(null)
  const requests = useRef(new Map())
  const nodes = React.Children.toArray(children)
  useEffect(() => {
    const keys = new Set(nodes.map(node => node.key))
    for (const [key, owner] of requests.current) {
      if (!keys.has(key)) {
        owner.action?.dispose()
        requests.current.delete(key)
      }
    }
  })
  useEffect(
    () => () => {
      requests.current.forEach(owner => {
        owner.action?.dispose()
        owner.action = undefined
        owner.state = undefined
      })
    },
    []
  )
  const small = useMedia('(max-width: 575.98px)')
  const label = suppliedLabel
    ? suppliedLabel
    : subject
    ? window.MEButtons.text('strSubjectSelected', { subject, count })
    : window.MEButtons.text('strSelected', { count })
  const clear = useRef(onClear)
  clear.current = onClear
  const working = useRef(busy)
  working.current = busy
  const previousCount = useRef(0)
  useEffect(() => {
    if (count === previousCount.current) return
    previousCount.current = count
    const timer = setTimeout(() => window.MEButtons.announce(label), 500)
    return () => clearTimeout(timer)
  }, [label])
  useEffect(() => {
    if (!count) return
    const escape = event => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        working.current ||
        !event.target.closest ||
        window.MEButtons.hasOpenLayer()
      )
        return
      // One Escape clears one toolbar: the one holding focus, or else the
      // first visible one, as on server-rendered pages.
      const stack = root.current.closest('.me-floating-bar-stack')
      if (!stack) return
      const contentId = stack.dataset.contentRegion
      const content = document.getElementById(contentId) || document.body
      const owner = event.target.closest('.me-bulk-action-toolbar')
      const first =
        stack.querySelector('.me-bulk-action-toolbar:not([hidden])') ===
        root.current
      if (
        owner
          ? owner === root.current
          : first &&
            (content.contains(event.target) || stack.contains(event.target))
      ) {
        event.preventDefault()
        clear.current()
      }
    }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [count])
  // Folding remounts an action elsewhere; its request owner keeps the pending
  // state across the move.
  const actions = nodes.map(node => {
    if (!requests.current.has(node.key)) requests.current.set(node.key, {})
    return {
      node: React.cloneElement(node, {
        requestOwner: requests.current.get(node.key),
      }),
      primary: node.props.className?.includes('me-btn-primary'),
      // An inline confirm guards a destructive action just as data-confirm does.
      danger:
        node.props['data-confirm'] !== undefined || node.type === InlineConfirm,
    }
  })
  const { visible, overflow } = window.MEButtons.foldActions(
    actions,
    small ? 2 : 3
  )
  const primary = visible.find(action => action.primary)
  let separated = false
  return (
    <div
      ref={root}
      hidden={hidden}
      className={`me-floating-bar me-bulk-action-toolbar${
        leaving ? ' is-leaving' : ''
      }`}
      role="region"
      aria-label={window.MEButtons.text('strBulkActions')}
      aria-busy={busy || undefined}
    >
      <div className="me-bulk-action-left">
        <span data-selection-label="">{label}</span>
        <div className="me-floating-bar-actions">
          {visible
            .filter(action => action !== primary)
            .map(action => action.node)}
          {primary?.node}
          {/* The overflow trigger follows the kept actions, just before Clear;
              its menu opens from its end edge. */}
          {!!overflow.length && (
            <Menu
              id={id}
              label={window.MEButtons.text('strMoreActions')}
              iconOnly
              align="end"
              className="me-btn-icon me-btn-icon-lg"
              disabled={!count}
              aria-disabled={busy || undefined}
            >
              {overflow.map(({ node, danger }) => {
                const separator = danger && !separated
                if (danger) separated = true
                return [
                  separator && (
                    <div
                      key={`${node.key}-separator`}
                      role="separator"
                      className="me-menu-sep"
                    />
                  ),
                  React.cloneElement(
                    node,
                    {
                      className: `me-menu-item${danger ? ' is-danger' : ''}`,
                    },
                    node.props['data-label'] || node.props.children
                  ),
                ]
              })}
            </Menu>
          )}
        </div>
      </div>
      <Button
        className="me-btn-icon me-btn-icon-sm me-floating-bar-end"
        icon="fa-regular fa-xmark"
        aria-label={window.MEButtons.text('strClearSelection')}
        data-tooltip={window.MEButtons.text('strClearSelection')}
        aria-disabled={busy || undefined}
        onClick={onClear}
      />
    </div>
  )
}

BulkActionToolbar.propTypes = {
  count: PropTypes.number.isRequired,
  // Names the selection when a page shows more than one bulk bar ("Courses").
  subject: PropTypes.string,
  // Replaces the count text for a selection with its own words ("All 120
  // matching selected").
  label: PropTypes.string,
  // Work on the selection is running: the toolbar reads aria-busy, and Clear,
  // Escape and the overflow trigger are unavailable until it settles.
  busy: PropTypes.bool,
  onClear: PropTypes.func.isRequired,
  // Keyed Buttons; the toolbar folds the overflow into its overflow menu.
  children: PropTypes.node,
}
